// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Renders one asynchronous query through the complete state machine.
 *
 * Every administration screen routes its data through this component, so the
 * loading, error, empty, and success states required by
 * `docs/10-TESTING.md` §30 are guaranteed rather than remembered. A screen that
 * forgets a state is not possible: the states live here.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { ApiProblemError } from '../api-clients/problem';
import { EmptyState, ErrorState, LoadingState } from './StateViews';

/** Props for {@link AsyncSection}. */
export interface AsyncSectionProps<TData> {
  /** The query whose lifecycle is being rendered. */
  readonly query: UseQueryResult<TData, unknown>;
  /** Accessible description used while loading. */
  readonly loadingLabel: string;
  /** Heading shown when the query succeeded with no items. */
  readonly emptyTitle: string;
  /** Optional guidance shown alongside {@link AsyncSectionProps.emptyTitle}. */
  readonly emptyDescription?: string;
  /** Reports whether resolved data should be treated as empty. */
  readonly isEmpty?: (data: TData) => boolean;
  /** Renders the resolved, non-empty data. */
  readonly children: (data: TData) => ReactNode;
}

function asApiProblemError(error: unknown): ApiProblemError {
  return error instanceof ApiProblemError
    ? error
    : new ApiProblemError('The request could not be completed.', {
        kind: 'server',
        status: 0,
        cause: error,
      });
}

/** Render a query as loading, error, empty, or success. */
export function AsyncSection<TData>({
  query,
  loadingLabel,
  emptyTitle,
  emptyDescription,
  isEmpty,
  children,
}: AsyncSectionProps<TData>): ReactNode {
  if (query.isPending) {
    return <LoadingState label={loadingLabel} />;
  }
  if (query.isError) {
    return (
      <ErrorState
        error={asApiProblemError(query.error)}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  const data = query.data;
  if (isEmpty?.(data) === true) {
    return (
      <EmptyState
        title={emptyTitle}
        {...(emptyDescription === undefined ? {} : { description: emptyDescription })}
      />
    );
  }
  return <>{children(data)}</>;
}
